import React from "react";
import { Button, Flex, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { GOLD_BORDER, GOLD_TEXT, GOLD_LIGHT } from "constants/brandColors";

// First, last, and a window around the current page; `null` marks a "…" gap.
function visiblePages(current, total) {
  const wanted = new Set([1, total]);
  for (let p = current - 1; p <= current + 1; p += 1) {
    if (p >= 1 && p <= total) wanted.add(p);
  }
  if (current <= 3) [2, 3].forEach((p) => p <= total && wanted.add(p));
  const sorted = [...wanted].sort((a, b) => a - b);
  const result = [];
  sorted.forEach((page, i) => {
    if (i > 0 && page - sorted[i - 1] > 1) result.push(null);
    result.push(page);
  });
  return result;
}

// "Page 1 of 15 - Showing 1 to 10 results" + First / Previous / 1 2 3 … 15 / Next / Last
export default function TablePagination({ currentPage, totalPages, totalItems, perPage, onPageChange }) {
  const { t } = useTranslation();
  if (!totalPages || totalPages < 1) return null;

  const from = totalItems === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const to = Math.min(currentPage * perPage, totalItems);

  const pillProps = {
    size: "sm",
    variant: "outline",
    borderRadius: "full",
    borderColor: GOLD_BORDER,
    color: GOLD_TEXT,
    _hover: { bg: GOLD_LIGHT },
  };

  return (
    <Flex direction="column" align="center" gap={2} mt={4} mb={4} px={2}>
      <Text fontSize="sm" color="gray.600" textAlign="center">
        {t("Page")} {currentPage} {t("of")} {totalPages}
        {typeof totalItems === "number" && ` - ${t("Showing")} ${from} ${t("to")} ${to} ${t("of")} ${totalItems} ${t("results")}`}
      </Text>
      <Flex wrap="wrap" justify="center" gap={2}>
        <Button {...pillProps} isDisabled={currentPage <= 1} onClick={() => onPageChange(1)}>
          {t("First")}
        </Button>
        <Button {...pillProps} isDisabled={currentPage <= 1} onClick={() => onPageChange(currentPage - 1)}>
          {t("Previous")}
        </Button>
        {visiblePages(currentPage, totalPages).map((page, i) =>
          page === null ? (
            <Text key={`gap-${i}`} alignSelf="center" color="gray.500" px={1}>
              …
            </Text>
          ) : (
            <Button
              key={page}
              {...pillProps}
              minW="36px"
              bg={page === currentPage ? GOLD_BORDER : "white"}
              color={page === currentPage ? "#1A1A1A" : GOLD_TEXT}
              _hover={{ bg: page === currentPage ? GOLD_BORDER : GOLD_LIGHT }}
              aria-current={page === currentPage ? "page" : undefined}
              onClick={() => onPageChange(page)}
            >
              {page}
            </Button>
          )
        )}
        <Button {...pillProps} isDisabled={currentPage >= totalPages} onClick={() => onPageChange(currentPage + 1)}>
          {t("Next")}
        </Button>
        <Button {...pillProps} isDisabled={currentPage >= totalPages} onClick={() => onPageChange(totalPages)}>
          {t("Last")}
        </Button>
      </Flex>
    </Flex>
  );
}
